import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError, timer } from 'rxjs';
import { exhaustMap, retry, startWith, takeWhile } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { UpdateTagByDataviewDTO } from '@tj/tj-dmp-lib-api';

/** Avancement d'un tag en masse que l'engine applique en tâche de fond. */
export interface TagJobStatus {
    jobId: string;
    status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';
    /** Contacts de la recherche ; `null` tant qu'ils ne sont pas encore lus. */
    totalPersonals: number | null;
    processedPersonals: number;
    /** Couples contact / tag réellement modifiés jusqu'ici. */
    changes: number;
    errorMessage?: string | null;
}

export function isTagJobFinished(job: TagJobStatus): boolean {
    return job.status === 'COMPLETED' || job.status === 'FAILED';
}

/** Avancement en pourcentage ; `null` tant que le nombre de contacts n'est pas connu. */
export function tagJobPercent(job: TagJobStatus | null): number | null {
    if (!job || job.totalPersonals == null) {
        return null;
    }
    return job.totalPersonals === 0 ? 100 : Math.floor(job.processedPersonals * 100 / job.totalPersonals);
}

/** Première interrogation rapprochée : un tag sur quelques centaines de contacts est déjà fini. */
const FIRST_POLL_MS = 500;
const POLL_INTERVAL_MS = 1500;

/**
 * Une coupure passagère (réseau, gateway) ne fait pas perdre le suivi d'une tâche qui continue. Une
 * réponse de l'engine, elle, est définitive : un 404 dit que la tâche n'est plus suivie.
 */
function retryIfUnreachable(error: unknown): Observable<unknown> {
    const unreachable = error instanceof HttpErrorResponse && (error.status === 0 || error.status >= 502);
    return unreachable ? timer(POLL_INTERVAL_MS) : throwError(() => error);
}

/**
 * Tags en masse appliqués en tâche de fond par l'engine : l'appel de lancement répond tout de suite,
 * l'avancement se suit par {@link watch}. Sur ~100 000 contacts, l'application dure trop pour tenir dans
 * une requête, et l'écran n'a pas à rester bloqué pendant ce temps.
 */
@Injectable({ providedIn: 'root' })
export class TagJobService {
    private readonly baseUrl = environment.SERVER_API_URL + 'services/engine/api';

    constructor(private http: HttpClient) {}

    /** Tag des contacts d'une recherche ; la règle éventuelle est enregistrée avant la réponse. */
    startTagByDataview(tenant: string, partner: string, body: UpdateTagByDataviewDTO): Observable<TagJobStatus> {
        return this.http.post<TagJobStatus>(`${this.baseUrl}/tag-event-dataview/jobs`, body,
            { headers: this.headers(tenant, partner) });
    }

    /** Exécution d'une règle de tag. Sans affilié, l'engine prend celui de la règle. */
    startRun(tenant: string, partner: string | null, idTagEvent: number): Observable<TagJobStatus> {
        return this.http.post<TagJobStatus>(`${this.baseUrl}/tag-event-personals/${encodeURIComponent(String(idTagEvent))}/run/jobs`,
            null, { headers: this.headers(tenant, partner) });
    }

    getJob(tenant: string, jobId: string): Observable<TagJobStatus> {
        return this.http.get<TagJobStatus>(`${this.baseUrl}/tag-event-jobs/${encodeURIComponent(jobId)}`,
            { headers: this.headers(tenant, null) });
    }

    /**
     * Avancement d'une tâche lancée, jusqu'à sa fin : la dernière valeur est `COMPLETED` ou `FAILED`.
     * Se désabonner arrête le suivi, pas la tâche.
     */
    watch(tenant: string, job: TagJobStatus): Observable<TagJobStatus> {
        if (isTagJobFinished(job)) {
            return of(job);
        }
        return timer(FIRST_POLL_MS, POLL_INTERVAL_MS).pipe(
            exhaustMap(() => this.getJob(tenant, job.jobId).pipe(retry({ count: 2, delay: retryIfUnreachable }))),
            takeWhile(status => !isTagJobFinished(status), true),
            startWith(job)
        );
    }

    private headers(tenant: string, partner: string | null): HttpHeaders {
        const headers = new HttpHeaders().set('X-DataStoreID', tenant);
        return partner ? headers.set('X-PARTNER-ID', partner) : headers;
    }
}
